import { TableColumnsType } from "antd";
import { getAlertHistoryMergedList } from "api/alertAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  AlertHistoryList,
  GetAlertHistoryListResponse,
} from "api/interfaces/alertInterface.interface";
import { BasePagination } from "api/interfaces/commonInterface.interface";
import InfiniteExpandTable from "components/blocks/table/InfiniteExpandTable";
import useApiError from "hook/useApiError";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useAppSelector } from "redux/hooks";
import { baseQueryInfo } from "utils/queryUtil";

export default function AlertExpandContainer({
  mergedSystemId,
  queryKey,
  columns,
  onRowClick,
}: {
  mergedSystemId: string;
  queryKey: BasePagination;
  columns: TableColumnsType<AlertHistoryList>;
  onRowClick: (row: AlertHistoryList) => void;
}) {
  const { lang, notifyError } = useApiError();
  const [mergedSystemQueryInfo, setMergedSystemQueryInfo] =
    useState<BasePagination>({
      ...baseQueryInfo,
      sortDirection: queryKey.sortDirection,
    });
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const onInitialData = () => {
    setMergedSystemQueryInfo({
      ...queryKey,
    });
  };
  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ["alertHistoryMerged", queryKey, mergedSystemId, mergedSystemQueryInfo],
    ({ pageParam = 0 }) =>
      getAlertHistoryMergedList({
        accountId: selectedAccount.accountId,
        systemId: mergedSystemId as string,
        payload: {
          ...mergedSystemQueryInfo,
          pageNum: pageParam,
        },
      }),
    {
      getNextPageParam: (lastPage: GetAlertHistoryListResponse | undefined) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onError: (e: any) => {
        notifyError(lang.label.alertHistory.notify.subSystem.fail);
        onInitialData();
      },
    }
  );

  const mergedColumns = useMemo(() => {
    return columns.filter((col) => col.key !== "type");
  }, [columns]);

  const list = useMemo(() => {
    return (data?.pages || []).flatMap((page: GetAlertHistoryListResponse) =>
      (page.result?.alertRule ?? []).map((item) => ({
        ...item,
        name: item.recorderName,
        cloudSystemId: "",
        recorderCount: 1,
      }))
    );
  }, [data]);

  useEffect(() => {
    setMergedSystemQueryInfo((info) => ({
      ...info,
      sortDirection: queryKey.sortDirection,
      sortType:
        queryKey.sortType === "mergedSystemName" ? "name" : queryKey.sortType,
      pageNum: 0,
    }));
  }, [queryKey.sortDirection, queryKey.sortType]);

  return (
    <InfiniteExpandTable
      data={list}
      loadMore={fetchNextPage}
      isLoading={isInitialLoading || isFetchingNextPage}
      hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
      columns={mergedColumns as TableColumnsType<AlertHistoryList>}
      leftPadding={150}
    />
  );
}
